import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { dateOnly } from "@/lib/format";
import { PageTitle } from "@/components/brand";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const FIELDS = [["daily_deposit_limit", "Daily deposit limit (₹)"], ["weekly_deposit_limit", "Weekly deposit limit (₹)"], ["monthly_deposit_limit", "Monthly deposit limit (₹)"], ["session_time_limit_min", "Session reminder (minutes)"]];

const active = (d) => d && new Date(d) > new Date();

export default function Settings() {
  const [rg, setRg] = useState(null);
  const [f, setF] = useState({});
  const [ex, setEx] = useState({ kind: "cooling_off", days: 7 });
  const [confirm, setConfirm] = useState(false);

  const apply = (d) => { setRg(d); setF(Object.fromEntries(FIELDS.map(([k]) => [k, d[k] ?? ""]))); };
  useEffect(() => { api.get("/me/responsible-gaming").then((r) => apply(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : Number(v)]));
    try { const { data } = await api.put("/me/responsible-gaming", body); apply(data); toast.success("Limits saved"); } catch (e2) { toast.error(apiError(e2)); }
  };
  const takeBreak = async () => {
    try { const { data } = await api.post("/me/responsible-gaming/exclusion", { kind: ex.kind, days: Number(ex.days) }); apply(data); toast.success("Break activated"); } catch (e) { toast.error(apiError(e)); }
    setConfirm(false);
  };

  return (
    <div data-testid="settings-page">
      <PageTitle crumbs={["Account", "Settings"]} title="Settings" subtitle="Responsible gaming controls and account preferences." />
      <div className="grid gap-6 lg:grid-cols-2">
        <form onSubmit={save} data-testid="rg-limits-form" className="card-cr space-y-4 p-6">
          <p className="font-display text-lg font-bold">Deposit & time limits</p>
          <p className="text-sm text-cr-muted">Deposits that would exceed a limit are blocked by the server. Leave blank for no limit.</p>
          {FIELDS.map(([k, l]) => (
            <div key={k}><label className="label-cr">{l}</label><input data-testid={`rg-${k}`} inputMode="numeric" className="input-cr" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value.replace(/[^\d]/g, "") })} /></div>
          ))}
          <button data-testid="rg-save-btn" className="btn-gold w-full">Save limits</button>
        </form>
        <div className="card-cr space-y-4 p-6">
          <p className="font-display text-lg font-bold">Take a break</p>
          {rg && active(rg.cooling_off_until) && <p data-testid="rg-cooling-active" className="rounded bg-cr-gold/10 p-3 text-sm text-cr-gold">Cooling-off active until {dateOnly(rg.cooling_off_until)}</p>}
          {rg && active(rg.self_excluded_until) && <p data-testid="rg-exclusion-active" className="rounded bg-cr-danger/10 p-3 text-sm text-cr-danger">Self-exclusion active until {dateOnly(rg.self_excluded_until)}</p>}
          <div><label className="label-cr">Type</label>
            <select data-testid="rg-break-kind" className="input-cr" value={ex.kind} onChange={(e) => setEx({ ...ex, kind: e.target.value })}>
              <option value="cooling_off">Cooling-off period</option><option value="self_exclusion">Self-exclusion</option>
            </select></div>
          <div><label className="label-cr">Duration</label>
            <select data-testid="rg-break-days" className="input-cr" value={ex.days} onChange={(e) => setEx({ ...ex, days: e.target.value })}>
              {(ex.kind === "cooling_off" ? [1, 7, 30] : [180, 365, 1825]).map((d) => <option key={d} value={d}>{d} days</option>)}
            </select></div>
          <p className="text-xs text-cr-muted">During a break, deposits are blocked. A break cannot be shortened once started.</p>
          <button data-testid="rg-break-btn" onClick={() => setConfirm(true)} className="btn-outline w-full text-cr-danger">Start break</button>
        </div>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-cr-line bg-cr-surface text-cr-ink">
          <AlertDialogHeader><AlertDialogTitle>Start {ex.kind === "cooling_off" ? "cooling-off" : "self-exclusion"} for {ex.days} days?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone early. Deposits will be blocked for the whole period.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="rg-break-cancel">Cancel</AlertDialogCancel><AlertDialogAction data-testid="rg-break-confirm" onClick={takeBreak} className="bg-cr-danger text-white">Confirm</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
